import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { approvalApi, type ApprovalRequest } from '@/lib/api';
import { usePortalSocketEvent } from '@/contexts/PortalSocketContext';
import { Clock, CheckCircle, X } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { PageGuide } from '@/components/PageGuide';

export const Route = createFileRoute('/_auth/approvals')({ component: ApprovalsPage });

type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'TIMED_OUT';

const STATUS_STYLE: Record<ApprovalStatus, { bg: string; text: string; border: string }> = {
  PENDING:   { bg: '#f59e0b20', text: '#fbbf24', border: '#f59e0b40' },
  APPROVED:  { bg: '#10b98120', text: '#34d399', border: '#10b98140' },
  REJECTED:  { bg: '#ef444420', text: '#f87171', border: '#ef444440' },
  TIMED_OUT: { bg: '#47556920', text: '#64748b', border: '#47556940' },
};

function useCountdown(expiresAt: string) {
  const [remaining, setRemaining] = useState(() => Math.max(0, new Date(expiresAt).getTime() - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setRemaining(Math.max(0, new Date(expiresAt).getTime() - Date.now())), 1000);
    return () => clearInterval(id);
  }, [expiresAt]);
  if (remaining === 0) return 'Expired';
  const m = Math.floor(remaining / 60000);
  const s = Math.floor((remaining % 60000) / 1000);
  return `${m}m ${s}s`;
}

const FILTERS: Array<{ value: ApprovalStatus | ''; label: string }> = [
  { value: 'PENDING',   label: 'Pending' },
  { value: '',          label: 'All' },
  { value: 'APPROVED',  label: 'Approved' },
  { value: 'REJECTED',  label: 'Rejected' },
  { value: 'TIMED_OUT', label: 'Timed out' },
];

export default function ApprovalsPage() {
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<ApprovalStatus | ''>('PENDING');

  const { data: approvals = [], isLoading } = useQuery({
    queryKey: ['approvals', statusFilter],
    queryFn: () => approvalApi.list(statusFilter || undefined),
    // no polling — socket invalidates
  });

  // Real-time: any counts update means an approval changed
  usePortalSocketEvent('counts:update', () => {
    qc.invalidateQueries({ queryKey: ['approvals'] });
  });
  usePortalSocketEvent('approval:new', () => {
    qc.invalidateQueries({ queryKey: ['approvals'] });
  });

  const pendingCount = approvals.filter((a) => a.status === 'PENDING').length;

  return (
    <div className="page">
      <PageGuide pageKey="approvals" />

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Approval Queue</h1>
            {pendingCount > 0 && (
              <span style={{ padding: '2px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#f59e0b20', color: '#fbbf24', border: '1px solid #f59e0b40' }}>
                {pendingCount} pending
              </span>
            )}
          </div>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>Skill execution requests awaiting your authorisation</p>
        </div>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 8, marginTop: 20, flexWrap: 'wrap' }}>
        {FILTERS.map((f) => (
          <button key={f.value || 'ALL'} onClick={() => setStatusFilter(f.value)} style={{
            padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid',
            borderColor: statusFilter === f.value ? 'var(--color-primary)' : '#1e2130',
            background: statusFilter === f.value ? 'var(--color-primary-dim)' : 'transparent',
            color: statusFilter === f.value ? 'var(--color-primary)' : '#64748b',
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {!isLoading && approvals.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <CheckCircle size={32} style={{ opacity: 0.5, marginBottom: 12, color: '#34d399' }} />
          <p style={{ margin: 0, fontSize: 14 }}>
            {statusFilter === 'PENDING' ? 'No pending approvals — agents are running autonomously.' : 'No requests match this filter.'}
          </p>
        </div>
      )}

      {!isLoading && approvals.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {approvals.map((a) => <ApprovalCard key={a.id} approval={a} />)}
        </div>
      )}
    </div>
  );
}

function ApprovalCard({ approval }: { approval: ApprovalRequest }) {
  const qc = useQueryClient();
  const countdown = useCountdown(approval.expiresAt);
  const isPending = approval.status === 'PENDING';
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const sty = STATUS_STYLE[approval.status as ApprovalStatus] ?? STATUS_STYLE.TIMED_OUT;
  const isExpiringSoon = isPending && new Date(approval.expiresAt).getTime() - Date.now() < 5 * 60_000;

  const approveMutation = useMutation({
    mutationFn: () => approvalApi.approve(approval.id),
    onSuccess: () => { toast.success('Approved'); qc.invalidateQueries({ queryKey: ['approvals'] }); },
  });
  const rejectMutation = useMutation({
    mutationFn: () => approvalApi.reject(approval.id, rejectReason),
    onSuccess: () => { toast.success('Rejected'); qc.invalidateQueries({ queryKey: ['approvals'] }); setShowReject(false); },
  });

  return (
    <div id={`approval-${approval.id}`} style={{
      borderRadius: 12, border: `1px solid ${isExpiringSoon ? '#ef444440' : '#1e2130'}`,
      background: '#0a0e1c', padding: '16px 20px',
    }}>
      {/* Row 1: badges + meta */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10, flexWrap: 'wrap' }}>
        <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sty.bg, color: sty.text, border: `1px solid ${sty.border}` }}>
          ● {approval.status}
        </span>
        <span style={{ fontSize: 11, color: '#a5b4fc', fontFamily: 'monospace', background: '#6366f115', padding: '2px 8px', borderRadius: 10 }}>
          {approval.skillId.slice(0, 8)}…
        </span>
        {isPending && (
          <span style={{ fontSize: 11, color: isExpiringSoon ? '#f87171' : '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Clock size={11} /> {countdown}{isExpiringSoon ? ' ⚠' : ''}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 11, color: '#475569' }}>
          {new Date(approval.createdAt).toLocaleString('en-GB')}
        </span>
      </div>

      {/* User message */}
      {approval.userMessage && (
        <p style={{ margin: '0 0 10px', fontSize: 13, color: '#e2e8f0', lineHeight: 1.5 }}>
          <span style={{ fontSize: 11, color: '#475569', display: 'block', marginBottom: 2 }}>User request:</span>
          {approval.userMessage}
        </p>
      )}

      <div style={{ fontSize: 11, color: '#475569', marginBottom: 12, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span>Conv: <code style={{ color: '#64748b' }}>{approval.conversationId.slice(0, 8)}…</code></span>
        {approval.rejectionReason && <span style={{ color: '#f87171' }}>Rejection: {approval.rejectionReason}</span>}
      </div>

      {/* Actions */}
      {isPending && (
        <>
          {showReject ? (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Reason (optional)"
                style={{ flex: 1, padding: '7px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none' }}
              />
              <button onClick={() => rejectMutation.mutate()} style={{ padding: '7px 14px', borderRadius: 8, background: '#ef444420', border: '1px solid #ef444440', color: '#f87171', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                Confirm
              </button>
              <button onClick={() => setShowReject(false)} style={{ padding: '7px 14px', borderRadius: 8, background: 'transparent', border: '1px solid #1e2130', color: '#64748b', fontSize: 12, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                id={`approve-${approval.id}`}
                onClick={() => approveMutation.mutate()}
                disabled={approveMutation.isPending}
                style={{ padding: '7px 18px', borderRadius: 8, background: '#10b98120', border: '1px solid #10b98140', color: '#34d399', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                {approveMutation.isPending ? <Spinner size="sm" /> : <><CheckCircle size={13} /> Approve</>}
              </button>
              <button
                id={`reject-${approval.id}`}
                onClick={() => setShowReject(true)}
                style={{ padding: '7px 18px', borderRadius: 8, background: '#ef444420', border: '1px solid #ef444440', color: '#f87171', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <X size={13} /> Reject
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
